import { Outlet, NavLink, Link } from "react-router-dom";
import { useFan } from "@/lib/fanContext";
import { Compass, Heart, Ticket, User, Home, Building2 } from "lucide-react";

// Passport-only tabs. "Discover" is intentionally NOT here — the passport
// Discover tab exits back to the main Project Mint Home/Discover so the fan
// never gets stranded inside the passport shell.
const TABS = [
    { to: "/fan", label: "Overview", icon: Home, end: true },
    { to: "/fan/following", label: "Following", icon: Heart },
    { to: "/fan/venues", label: "Venues", icon: Building2 },
    { to: "/fan/events", label: "My Events", icon: Ticket },
    { to: "/", label: "Discover", icon: Compass, external: true },
    { to: "/fan/profile", label: "Profile", icon: User },
];

export default function FanLayout() {
    const { fan, loading } = useFan();

    return (
        <div className="pm-fade" data-testid="fan-layout">
            <header className="border-b border-[#2a2a2a]">
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 pt-8 md:pt-12 pb-4 md:pb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <div className="flex items-center gap-4">
                        {fan?.image_url && (
                            <img src={fan.image_url} alt="" className="w-16 h-16 md:w-20 md:h-20 object-cover grayscale border border-[#2a2a2a]" data-testid="fan-avatar" />
                        )}
                        <div>
                            {/* "Fan Passport" kicker doubles as a way back to Project Mint Home */}
                            <Link
                                to="/"
                                className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400] hover:text-[#f4f4f0] inline-flex items-center gap-1"
                                data-testid="fan-header-home-link"
                                title="Back to Project Mint"
                            >
                                Fan Passport
                                <span className="text-[#666]">·</span>
                                <span className="text-[10px] font-mono uppercase text-[#a3a3a3]">Project Mint ↩</span>
                            </Link>
                            <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-5xl leading-none" data-testid="fan-name-heading">
                                {loading ? "…" : fan?.name || "Guest"}
                            </h1>
                            {fan && (
                                <div className="mt-1 text-xs font-mono uppercase text-[#a3a3a3]">
                                    {fan.city || "—"}{fan.country ? `, ${fan.country}` : ""}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <nav className="max-w-[1440px] mx-auto px-4 md:px-6 flex gap-1 md:gap-2 overflow-x-auto no-scrollbar">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        return (
                            <NavLink
                                key={t.label}
                                to={t.to}
                                end={t.end}
                                data-testid={`fan-tab-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `flex items-center gap-2 px-3 md:px-4 py-3 border-b-2 font-display font-bold uppercase tracking-wider text-xs md:text-sm whitespace-nowrap transition-colors ${
                                        // The Discover tab is a "return to main site" affordance — never mark it active
                                        // when the fan is inside /fan/*, otherwise it looks like a passport-local page.
                                        t.external
                                            ? "border-transparent text-[#a3a3a3] hover:text-[#f4f4f0]"
                                            : isActive
                                                ? "border-[#ff4400] text-[#ff4400]"
                                                : "border-transparent text-[#a3a3a3] hover:text-[#f4f4f0]"
                                    }`
                                }
                            >
                                <Icon className="w-4 h-4" />
                                {t.label}
                            </NavLink>
                        );
                    })}
                </nav>
            </header>

            <main className="max-w-[1440px] mx-auto">
                <Outlet />
            </main>
        </div>
    );
}
